import { trackShare } from "../lib/analytics";
import SponsorInlineOro from "../components/SponsorInlineOro";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Copy, Check } from "lucide-react";
import { supabase } from "../lib/supabase";

export default function NewsDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    supabase.from("news").select("*").eq("id", id).single()
      .then(({ data }) => setArticle(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!article) return;
    document.title = article.title + " – ASD Rubiana Calcio";
    const setMeta = (property, content, isName = false) => {
      const attr = isName ? "name" : "property";
      let el = document.querySelector(`meta[${attr}="${property}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, property); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("og:title", article.title);
    setMeta("og:description", article.excerpt || "");
    setMeta("og:image", article.image || "");
    setMeta("og:url", window.location.href);
    setMeta("og:type", "article");
    setMeta("twitter:card", "summary_large_image", true);
    setMeta("twitter:title", article.title, true);
    setMeta("twitter:description", article.excerpt || "", true);
    setMeta("twitter:image", article.image || "", true);
    return () => { document.title = "ASD Rubiana Calcio – Squadra & Palestra"; };
  }, [article]);

  const pageUrl = window.location.href;
  const pageTitle = article?.title || "ASD Rubiana Calcio";

  const copyLink = () => {
    navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

 const openShare = (network) => {
  const url = encodeURIComponent(pageUrl);
  const text = encodeURIComponent(pageTitle + " " + pageUrl);
  const links = {
    facebook: "https://www.facebook.com/sharer/sharer.php?u=" + url,
    whatsapp: "https://wa.me/?text=" + text,
    x: "https://x.com/intent/tweet?text=" + encodeURIComponent(pageTitle) + "&url=" + url,
  };
  trackShare(network, pageTitle);
  window.open(links[network], "_blank", "noopener,noreferrer");
};

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="h-8 w-8 rounded-full border-2 border-yellow-400 border-t-transparent animate-spin" />
    </div>
  );

  if (!article) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <p className="text-zinc-500">Articolo non trovato.</p>
      <Link to="/news" className="text-yellow-400 text-sm mt-4 inline-block">← Torna alle news</Link>
    </div>
  );

  return (
    <div data-testid="news-detail-page" style={{ backgroundColor: "#002749" }}>
      {article.image && (
        <div className="w-full max-w-2xl mx-auto px-4 pt-8">
          <img src={article.image} alt={article.title} className="w-full h-auto rounded-2xl object-contain" />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/news" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-yellow-400 transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> Tutte le news
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <span className="text-overline text-yellow-400">{article.category}</span>
          <span className="text-xs text-zinc-500">
            {new Date(article.published_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
          </span>
        </div>

        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase leading-tight mb-6">
          {article.title}
        </h1>

        {article.excerpt && (
          <p className="text-lg text-zinc-300 leading-relaxed mb-8 border-l-2 border-yellow-400 pl-4">
            {article.excerpt}
          </p>
        )}

        <SponsorInlineOro />
        <div className="prose prose-invert prose-zinc max-w-none text-zinc-300 leading-relaxed whitespace-pre-wrap text-base mb-12">
          {article.body}
        </div>

        {article.images && article.images.length > 0 && (
          <div className="mb-12">
            <div className="text-overline text-yellow-400 mb-4">// Galleria</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {article.images.map((url, idx) => (
                <img
                  key={idx}
                  src={url}
                  alt={`${article.title} - foto ${idx + 1}`}
                  className="w-full h-40 object-cover rounded-xl cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setLightboxImg(url)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="mb-10 rounded-2xl bg-yellow-400 p-6 sm:p-8 text-center">
  <h3 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight text-zinc-950 mb-2">
    Non perderti nessuna notizia!
  </h3>
  <p className="text-zinc-900/80 mb-5 text-sm sm:text-base">
    Iscriviti alla newsletter di ASD Rubiana Calcio e resta sempre aggiornato.
  </p>
  <Link
    to="/newsletter"
    className="inline-flex items-center justify-center px-8 py-3 rounded-full bg-zinc-950 text-yellow-400 font-display font-bold text-sm uppercase tracking-wide hover:bg-zinc-800 transition-colors"
  >
    Iscriviti alla Newsletter
  </Link>
</div>

        <div className="border-t border-zinc-800 pt-8">
          <div className="text-overline text-yellow-400 mb-4">// Condividi</div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => openShare("facebook")} className="px-5 py-2.5 rounded-full bg-[#1877F2] text-white font-display font-bold text-sm hover:opacity-90 transition-opacity">
              Facebook
            </button>
            <button onClick={() => openShare("whatsapp")} className="px-5 py-2.5 rounded-full bg-[#25D366] text-white font-display font-bold text-sm hover:opacity-90 transition-opacity">
              WhatsApp
            </button>
            <button onClick={() => openShare("x")} className="px-5 py-2.5 rounded-full bg-zinc-950 border border-zinc-700 text-white font-display font-bold text-sm hover:border-zinc-400 transition-colors">
              X (Twitter)
            </button>
            <button onClick={copyLink} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-zinc-900 border border-zinc-700 text-white font-display font-bold text-sm hover:border-zinc-400 transition-colors">
              {copied ? <Check className="h-4 w-4 text-yellow-400" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copiato!" : "Copia link"}
            </button>
          </div>
        </div>
      </div>

      {lightboxImg && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImg(null)}
        >
          <img src={lightboxImg} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}